import { Formatters } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { Set } from '../../ts/models/Set';
import { createMultipleItemResultNotificationValues, IErrorDetail, } from './ActionMessages';
import { ContentActionNotification, } from './ContentActionNotification';
import { IErrorItem, MultipleItemResult } from './MultipleItemResult';


/**
 * Translated messages that are displayed on success or error of the action, in one of the usual
 * cases. These are usually used in conjunction with `standardActionSuccessMsg` and
 * `standardActionErrorMsg` for strings that contain the relevant interpolations.
 */
export interface StandardActionToastMessages<T = unknown> {
  success: (inputs: { successCount: number, firstSuccessfulItem: T }) => string;
  error: (inputs: { errorCount: number, firstErrorItem: T }) => string;
}

/**
 * Helper function that interpolates the following values into an action success message:
 * ITEM_COUNT, ITEM_COUNT_LOC, ITEM_NAME.
 */
export function standardActionSuccessMsg(
  messagesFn: (a: { ITEM_COUNT: number, ITEM_COUNT_LOC: string, ITEM_NAME: string }) => string
): StandardActionToastMessages<{ name?: string }>['success'] {
  return ({ successCount, firstSuccessfulItem }) => messagesFn({
    ITEM_COUNT: successCount,
    ITEM_COUNT_LOC: Formatters.formatCount(successCount),
    ITEM_NAME: L10n.elideIfLong(firstSuccessfulItem && firstSuccessfulItem.name),
  });
}

/**
 * Helper function that interpolates the following values into an action error message:
 * ITEM_COUNT, ITEM_COUNT_LOC, ITEM_NAME.
 */
export function standardActionErrorMsg(
  messagesFn: (a: { ERROR_COUNT: number, ERROR_COUNT_LOC: string, ITEM_NAME: string }) => string
): StandardActionToastMessages<{ name?: string }>['error'] {
  return ({ errorCount, firstErrorItem }) => messagesFn({
    ERROR_COUNT: errorCount,
    ERROR_COUNT_LOC: Formatters.formatCount(errorCount),
    ITEM_NAME: L10n.elideIfLong(firstErrorItem && firstErrorItem.name),
  });
}

/**
 * Translated messages that are displayed on success or error of the action, in one of the usual
 * cases. These can also be used in conjunction with `standardActionSuccessMsg` and
 * `standardActionErrorMsg` for strings that contain the relevant interpolations.
 */
export interface SimpleActionToastMessages {
  successMsg: (inputs: { successCount: number }) => string;
  errorMsg: (inputs: { errorCount: number }) => string;
}

/**
 * Helper function that interpolates the following values into an action success message:
 * ITEM_COUNT, ITEM_COUNT_LOC.
 */
export function simpleActionSuccessMsg(
  messagesFn: (a: { ITEM_COUNT: number, ITEM_COUNT_LOC: string }) => string
): SimpleActionToastMessages['successMsg'] {
  return ({ successCount }) => messagesFn({
    ITEM_COUNT: successCount,
    ITEM_COUNT_LOC: Formatters.formatCount(successCount),
  });
}

/**
 * Helper function that interpolates the following values into an action error message:
 * ITEM_COUNT, ITEM_COUNT_LOC.
 */
export function simpleActionErrorMsg(
  messagesFn: (a: { ERROR_COUNT: number, ERROR_COUNT_LOC: string }) => string
): SimpleActionToastMessages['errorMsg'] {
  return ({ errorCount }) => messagesFn({
    ERROR_COUNT: errorCount,
    ERROR_COUNT_LOC: Formatters.formatCount(errorCount),
  });
}

export namespace ActionHelpers {

  /**
   * Display success and/or error toasts for the most common of scenarios, with a basic fallback
   * for an unknown error.
   *
   * This is a wrapper around ContentActionNotification.displayItemizedToastsForResult configured
   * in a decent way for most actions.
   * @param items
   * @param result
   * @param translations
   * @param describeErrorDetail
   */
  export function displayStandardToastsForResult<T>(
    items: Set<T>,
    result: IResult,
    translations: StandardActionToastMessages<T>,
    describeErrorDetail: (errorItem: IErrorItem<T>) => IErrorDetail,
  ) {
    if (result?.errors?.length > 0 && result.errors[0].id === undefined) {
      ContentActionNotification.displayToastForGenericError(result.errors[0]);
    } else {
      const itemResult = new MultipleItemResult(items, result);
      const notificationValues = createMultipleItemResultNotificationValues(itemResult,
        {
          describeSuccessSummary: (successCount: number) =>
            translations.success({
              successCount,
              firstSuccessfulItem: itemResult.firstSuccessfulItem()
            }),
          describeErrorSummary: (errorCount: number) =>
            translations.error({ errorCount, firstErrorItem: itemResult.firstErrorItem() }),
          describeErrorDetail: describeErrorDetail
        }
      );
      ContentActionNotification.displayItemizedToastsForResult(notificationValues);
    }

    return result; // to allow promise chaining
  }

  // When performing most actions, keep the selection except remove any items that were not found.
  // This helps to avoid "ghost" items in your selection - items that are still in your selection but are not in the list.
  export function deselectNotFound<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsIn(ResultHelper.getErrorIdsWithCode(result, notFoundCode));
  }

  // When performing delete-like actions, clear the selection, except keep any items that encountered an error other than "not found".
  // This allows you to see the items that failed and try again or perform some other action.
  export function deselectAllExceptErrors<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsNotIn(ResultHelper.getErrorIds(result, { excludeCode: notFoundCode }));
  }
}
